/**
 * Medical record ("السيرة الطبية"): every visit, diagnosis and result in one
 * timeline, grouped by category.
 *
 * Replaces `renderPatientMedicalCV` and `openPatientCVCategory`.
 */

import { useMemo, useState } from 'react';
import {
  Badge,
  EmptyState,
  ErrorState,
  Icon,
  PageHeader,
  Panel,
  Spinner,
  Tabs,
} from '../../components/common';
import { MedicalFileViewer } from '../../components/common/MedicalFileViewer';
import { PrintMedicalRecordButton } from '../../components/common/PrintMedicalRecordButton';
import { ExternalRecords } from './ExternalRecords';
import { PatientMeasurements } from './Measurements';
import { medicalRecordApi } from '../../api/endpoints';
import { useAsync } from '../../hooks/useAsync';
import { useAuth } from '../../context/AuthContext';
import { usePatientBookings } from '../../hooks/useBookings';
import { usePatientPrescriptions } from '../../hooks/usePrescriptions';
import { collectResultFiles } from '../../domain/files';
import { isoToDisplayDate, arabicDayOf } from '../../domain/dates';
import { statusLabel, statusStyle, typeLabel } from '../../domain/bookings';

const TABS = [
  { id: 'timeline', label: 'الخط الزمني', icon: 'history' },
  { id: 'diagnoses', label: 'التشخيصات', icon: 'clipboard-list' },
  { id: 'results', label: 'النتائج والمرفقات', icon: 'folder-open' },
  // Documents from clinics that never used this system. Without this tab the
  // record only covers the part of a life that happened after signing up.
  { id: 'external', label: 'سجلات خارجية', icon: 'paperclip' },
  // المرحلة 1.2: every measurement, from every source, on one curve.
  { id: 'measurements', label: 'القياسات', icon: 'activity' },
];

export function PatientMedicalCV() {
  const { patientId, session } = useAuth();
  const { bookings, loading: bookingsLoading, error, reload } = usePatientBookings(patientId, {
    poll: false,
  });
  const { prescriptions, loading: prescriptionsLoading } = usePatientPrescriptions(patientId);

  // Fetched here as well as inside the tab, so the printed record carries them
  // whether or not the patient has opened that tab.
  const externalState = useAsync(() => medicalRecordApi.list(), [patientId], {
    initialData: [],
    enabled: Boolean(patientId),
  });

  const [tab, setTab] = useState('timeline');
  const [viewingFile, setViewingFile] = useState(null);

  const loading = bookingsLoading || prescriptionsLoading;

  /** Visits and prescriptions merged into one descending timeline. */
  const timeline = useMemo(() => {
    const entries = [];

    for (const booking of bookings) {
      entries.push({
        kind: 'visit',
        id: `booking-${booking.id}`,
        date: booking.date,
        title: booking.doctorName || 'زيارة',
        subtitle: [typeLabel(booking.type), booking.clinicName].filter(Boolean).join(' — '),
        status: booking.status,
      });
    }

    for (const prescription of prescriptions) {
      entries.push({
        kind: 'prescription',
        id: `prescription-${prescription.id}`,
        date: prescription.date,
        title: prescription.PrescribingDoctor?.name || prescription.doctorName || 'روشتة',
        subtitle: prescription.diagnosis || 'روشتة علاجية',
        payload: prescription,
      });
    }

    return entries.sort((a, b) => String(b.date).localeCompare(String(a.date)));
  }, [bookings, prescriptions]);

  const diagnoses = useMemo(
    () =>
      prescriptions
        .filter((p) => p.diagnosis)
        .map((p) => ({
          id: p.id,
          date: p.date,
          diagnosis: p.diagnosis,
          doctor: p.PrescribingDoctor?.name || p.doctorName || 'طبيب',
          notes: p.notes,
        })),
    [prescriptions],
  );

  /** Every uploaded lab/x-ray attachment across all prescriptions. */
  const results = useMemo(() => {
    const collected = [];
    for (const prescription of prescriptions) {
      const walk = (items, type) => {
        (items || []).forEach((item, index) => {
          const files = collectResultFiles(item);
          if (files.length === 0) return;
          collected.push({
            key: `${prescription.id}-${type}-${index}`,
            name: item.name,
            type,
            date: prescription.date,
            doctorComment: item.doctorComment,
            files,
          });
        });
      };
      walk(prescription.labTests, 'labTest');
      walk(prescription.xrays, 'xray');
    }
    return collected.sort((a, b) => String(b.date).localeCompare(String(a.date)));
  }, [prescriptions]);

  const tabsWithCounts = TABS.map((t) => ({
    ...t,
    count:
      t.id === 'timeline' ? timeline.length
      : t.id === 'diagnoses' ? diagnoses.length
      : t.id === 'external' ? (externalState.data || []).length
      : t.id === 'measurements' ? undefined
      : results.length,
  }));

  return (
    <div className="space-y-6">
      <PageHeader
        title="السجل الطبي"
        subtitle="كل زياراتك وتشخيصاتك ونتائج فحوصاتك في مكان واحد"
        icon="clipboard-list"
        actions={
          <PrintMedicalRecordButton
            patientId={patientId}
            patientName={session?.name}
            patientCode={session?.patientCode || session?.code || ''}
            patientPhone={session?.phone || ''}
            bookings={bookings}
            prescriptions={prescriptions}
            externalRecords={externalState.data || []}
            label="طباعة / حفظ PDF"
          />
        }
      />

      <Tabs tabs={tabsWithCounts} active={tab} onChange={setTab} />

      {error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : loading ? (
        <Spinner label="جاري تجميع السجل الطبي..." />
      ) : (
        <>
          {tab === 'timeline' && <TimelineView entries={timeline} />}
          {tab === 'diagnoses' && <DiagnosesView diagnoses={diagnoses} />}
          {tab === 'results' && <ResultsView results={results} onView={setViewingFile} />}
          {tab === 'external' && <ExternalRecords onChange={externalState.reload} />}
          {tab === 'measurements' && <PatientMeasurements />}
        </>
      )}

      <MedicalFileViewer
        open={Boolean(viewingFile)}
        onClose={() => setViewingFile(null)}
        file={viewingFile}
      />
    </div>
  );
}

function TimelineView({ entries }) {
  if (entries.length === 0) {
    return (
      <Panel>
        <EmptyState
          icon="history"
          title="السجل فارغ"
          message="سيمتلئ هذا السجل تلقائياً مع كل زيارة وروشتة."
        />
      </Panel>
    );
  }

  return (
    <Panel>
      <ol className="relative space-y-5 border-s border-white/10 ps-6">
        {entries.map((entry) => (
          <li key={entry.id} className="relative">
            <span
              className={`absolute -start-[31px] top-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-slate-950 ${
                entry.kind === 'visit' ? 'bg-cyan-500' : 'bg-purple-500'
              }`}
            >
              <Icon name={entry.kind === 'visit' ? 'stethoscope' : 'file-text'} size={10} />
            </span>

            <div className="rounded-xl border border-white/5 bg-white/5 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-sm font-bold text-white">{entry.title}</h3>
                {entry.status && (
                  <Badge className={statusStyle(entry.status)}>{statusLabel(entry.status)}</Badge>
                )}
              </div>
              <p className="mt-0.5 text-xs text-slate-400">{entry.subtitle}</p>
              <p className="mt-1 text-[11px] text-slate-500">
                {arabicDayOf(entry.date)} — {isoToDisplayDate(entry.date) || entry.date}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </Panel>
  );
}

function DiagnosesView({ diagnoses }) {
  if (diagnoses.length === 0) {
    return (
      <Panel>
        <EmptyState icon="clipboard-x" title="لا توجد تشخيصات مسجلة" />
      </Panel>
    );
  }

  return (
    <div className="space-y-3">
      {diagnoses.map((entry) => (
        <Panel key={entry.id}>
          <div className="flex items-start gap-3">
            <span className="rounded-xl bg-amber-500/10 p-2.5 text-amber-400">
              <Icon name="clipboard-list" size={18} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold leading-relaxed text-white">{entry.diagnosis}</p>
              <p className="mt-1 text-xs text-slate-400">
                {entry.doctor} — {isoToDisplayDate(entry.date) || entry.date}
              </p>
              {entry.notes && (
                <p className="mt-2 rounded-lg bg-white/5 p-2 text-xs leading-relaxed text-slate-300">
                  {entry.notes}
                </p>
              )}
            </div>
          </div>
        </Panel>
      ))}
    </div>
  );
}

function ResultsView({ results, onView }) {
  if (results.length === 0) {
    return (
      <Panel>
        <EmptyState
          icon="folder-open"
          title="لا توجد نتائج مرفوعة"
          message="ارفع نتائج تحاليلك وأشعتك من صفحة الروشتات لتظهر هنا."
        />
      </Panel>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {results.map((result) => (
        <Panel key={result.key}>
          <div className="mb-3 flex items-start gap-3">
            <span
              className={`rounded-xl p-2.5 ${
                result.type === 'labTest'
                  ? 'bg-amber-500/10 text-amber-400'
                  : 'bg-indigo-500/10 text-indigo-400'
              }`}
            >
              <Icon name={result.type === 'labTest' ? 'flask-conical' : 'scan'} size={18} />
            </span>
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-sm font-bold text-white">{result.name}</h3>
              <p className="text-xs text-slate-400">
                {isoToDisplayDate(result.date) || result.date}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {result.files.map((file, index) => (
              <button
                key={`${result.key}-${index}`}
                type="button"
                onClick={() => onView(file)}
                className="flex items-center gap-1.5 rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1.5 text-[10px] text-emerald-300 transition-colors hover:bg-emerald-500/20"
              >
                <Icon name="file-check-2" size={13} />
                {file.name || `مرفق ${index + 1}`}
              </button>
            ))}
          </div>

          {result.doctorComment && (
            <p className="mt-3 rounded-lg border-s-2 border-indigo-500 bg-indigo-500/10 p-2 text-xs leading-relaxed text-indigo-200">
              <strong>تعليق الطبيب:</strong> {result.doctorComment}
            </p>
          )}
        </Panel>
      ))}
    </div>
  );
}

export default PatientMedicalCV;
